import { defineMessages, type MessageDescriptor } from 'react-intl'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LinePlayCircleIcon from 'LuxKit/Icons/line/LinePlayCircleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineClochIcon from 'LuxKit/Icons/line/LineClochIcon'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import { FlightsClassTypes } from 'constants/flight'

export interface CabinAmenity {
  icon: React.ComponentType
  message: MessageDescriptor
}

export interface CharterCabinContent {
  imageId: string
  inclusions: Array<CabinAmenity>
  baggage: Array<CabinAmenity>
}

const charterCabinMessages = defineMessages({
  economyStandardSeat: {
    id: 'F4yxnIGbGZ-HwUEN66LP',
    defaultMessage: 'Main cabin standard seat',
    description: 'Charter flight economy amenity: standard seat description',
  },
  entertainmentPersonalDevice: {
    id: 'jOMZWuWfYpzHHZnLuRO7',
    defaultMessage: 'Stream on-demand entertainment to your personal device',
    description: 'Charter flight amenity: in-flight entertainment streaming to personal device',
  },
  complimentaryMealAndDrinks: {
    id: 'BQRK_i0lCZoPiyXIpz2F',
    defaultMessage: 'Complimentary meal and drinks service',
    description: 'Charter flight amenity: complimentary meal and drinks',
  },
  carryOnSevenKg: {
    id: 'SBBJdZLWFogZDnxPBcZ6',
    defaultMessage: 'Carry-on 7kg',
    description: 'Charter flight standard carry-on baggage allowance',
  },
  checkedBagTwentyThreeKg: {
    id: 'dDAOzVfonIy_jZHM2Ry7',
    defaultMessage: 'Checked bag 23kg',
    description: 'Maldives charter flight standard checked baggage allowance',
  },
  premiumSeat: {
    id: 'bIUPAClOVQhzkpllLneO',
    defaultMessage: 'Extra legroom and generous seat recline for enhanced comfort',
    description: 'Charter flight economy plus amenity: enhanced seat comfort description',
  },
  priorityCheckInAndBoarding: {
    id: 'ISfGBY0rwClqd4bB8t7K',
    defaultMessage: 'Priority Check In & Boarding',
    description: 'Charter flight amenity: priority boarding in the flights flow',
  },
  luxRowSeat: {
    id: 'sJmNF4-E4hlFqCjc2bAV',
    defaultMessage: 'Extra space with four Economy seats in one row for two passengers',
    description: 'Charter flight Economy Extra amenity: extra space with four economy seats',
  },
  priorityCheckInSecurityAndBoarding: {
    id: 'QbpmHgAHGI-nie8wujID',
    defaultMessage: 'Priority Check In, Security & Boarding',
    description: 'Charter flight amenity: priority check in, security & boarding',
  },
  airportLounge: {
    id: '4c9FrzxtAUzvctMPi2aH',
    defaultMessage: 'Relax with airport lounge before your flight',
    description: 'Charter flight amenity: airport lounge access in the flights section',
  },
  luxRowSoloSeat: {
    id: 'xg8nM3c9mH2UEqgndWqR',
    defaultMessage: 'Extra space with four Economy seats in one row',
    description: 'Charter flight Economy Extra for one amenity: extra space solo variant',
  },
  businessSeat: {
    id: 'c0TjZdvglW0txcoTqdP0',
    defaultMessage: 'Luxury lie-flat seats with in-seat power for ultimate comfort and convenience',
    description: 'Charter flight business amenity: lie-flat seat description',
  },
  businessTransfer: {
    id: 'DrO4HKiA7kUqNDmfBNgi',
    defaultMessage: 'Home to airport transfers (max 40km) in MEL/SYD/BNE',
    description: 'Charter flight business amenity: home to airport transfer service',
  },
  businessEntertainment: {
    id: 'MHWh67Ugh3N8s2fNT9jR',
    defaultMessage: 'Stream on-demand entertainment to provided handheld entertainment screens',
    description: 'Charter flight business amenity: entertainment on provided screens',
  },
  businessDining: {
    id: '1BEZMl2YLOKp3GOUfM_T',
    defaultMessage: 'Gourmet dining options and drinks service',
    description: 'Charter flight business amenity: gourmet dining in the flights section',
  },
  dedicatedCabinCrew: {
    id: '9E_wC1ftCC91UWs2b2B3',
    defaultMessage: 'Dedicated cabin crew for personalised service',
    description: 'Charter flight business amenity: dedicated cabin crew',
  },
  businessCarryOn: {
    id: '3HU1zkF2KJRkfHNHGR4k',
    defaultMessage: 'Carry-on 2 pieces up to 14kg, max 10kg per piece',
    description: 'Charter flight business class carry-on baggage allowance',
  },
  businessCheckedBag: {
    id: 'nmBacWR105FebYmY5FEM',
    defaultMessage: 'Checked bag 2 pieces, total 46kg, no more than 32kg per piece',
    description: 'Charter flight business class checked baggage allowance',
  },
})

export const CHARTER_CABIN_CONTENT: Record<string, CharterCabinContent> = {
  [FlightsClassTypes.ECONOMY]: {
    imageId: 'maldives-economy',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.economyStandardSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.PREMIUM]: {
    imageId: 'maldives-economy-plus',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.premiumSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInAndBoarding,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.LUXROW]: {
    imageId: 'maldives-lux-row',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.luxRowSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInSecurityAndBoarding,
      },
      {
        icon: LineDoorIcon,
        message: charterCabinMessages.airportLounge,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.LUXROW_PAX_ONE]: {
    imageId: 'maldives-lux-row',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.luxRowSoloSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInSecurityAndBoarding,
      },
      {
        icon: LineDoorIcon,
        message: charterCabinMessages.airportLounge,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.BUSINESS]: {
    imageId: 'maldives-business',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.businessSeat,
      },
      {
        icon: LineCarIcon,
        message: charterCabinMessages.businessTransfer,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.businessEntertainment,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.businessDining,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInSecurityAndBoarding,
      },
      {
        icon: LineClochIcon,
        message: charterCabinMessages.dedicatedCabinCrew,
      },
      {
        icon: LineDoorIcon,
        message: charterCabinMessages.airportLounge,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.businessCarryOn,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.businessCheckedBag,
      },
    ],
  },
}

export { LineCarIcon }
